import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Box, FormLabel, Text } from '@chakra-ui/react'

interface FSGRecaptchaProps {
  label?: string
  isRequired?: boolean
  error?: string
  isInvalid?: boolean
  marginBottom?: string
  onChange?: (token: string | null) => void
  onExpired?: () => void
  onError?: () => void
  theme?: 'light' | 'dark'
  size?: 'compact' | 'normal'
}

export interface FSGRecaptchaRef {
  reset: () => void
  execute: () => void
  getValue: () => string | null
}

const FSGRecaptcha = forwardRef<FSGRecaptchaRef, FSGRecaptchaProps>(
  (
    {
      label,
      isRequired = false,
      error,
      isInvalid = false,
      marginBottom = '1rem',
      onChange,
      onExpired,
      onError,
      theme = 'light',
      size = 'normal',
    },
    ref,
  ) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null)

    // Get the site key from environment variables
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY

    useImperativeHandle(ref, () => ({
      reset: () => {
        recaptchaRef.current?.reset()
      },
      execute: () => {
        recaptchaRef.current?.execute()
      },
      getValue: () => {
        return recaptchaRef.current?.getValue() || null
      },
    }))

    if (!siteKey) {
      console.error(
        'reCAPTCHA site key is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY in your environment variables.',
      )
      return (
        <Box marginBottom={marginBottom}>
          <Text color='red.500' fontSize='sm'>
            reCAPTCHA configuration error. Please contact support.
          </Text>
        </Box>
      )
    }

    return (
      <Box marginBottom={marginBottom}>
        {label && (
          <FormLabel fontSize='md' fontWeight={600} color='brand.black.900' marginBottom='0.5rem'>
            {label}
            {isRequired && (
              <Text as='span' color='red.500' marginLeft='0.25rem'>
                *
              </Text>
            )}
          </FormLabel>
        )}

        <Box
          border={isInvalid ? '2px solid' : '1px solid'}
          borderColor={isInvalid ? 'red.500' : 'gray.200'}
          borderRadius='md'
          padding='0.5rem'
          display='inline-block'
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            onChange={onChange}
            onExpired={onExpired}
            onError={onError}
            theme={theme}
            size={size}
          />
        </Box>

        {error && isInvalid && (
          <Text color='red.500' fontSize='sm' marginTop='0.25rem'>
            {error}
          </Text>
        )}
      </Box>
    )
  },
)

FSGRecaptcha.displayName = 'FSGRecaptcha'

export { FSGRecaptcha }
